/**
 * Storage keys used by this app and the static auth-sync HTML pages in
 * `public/auth-sync/`. The HTML files cannot import from this module —
 * if you change a key here, mirror the change there.
 *
 * Per coding-standards §17: storage keys are domain literals and live in
 * one place.
 */
export const STORAGE_KEYS = {
  authToken: 'authToken',
  userData: 'userData',
  /**
   * Server-side user record with the real backend UUID. Parallel to Vue
   * ERP's `STORE_USERDETAIL` key (`auth.store.ts:14`). Written alongside
   * `userData` after login / handoff; consumers that need the canonical
   * `userId` UUID (e.g. permission endpoints) read from here rather than
   * `userData`, because `userData` carries the OAuth provider's profile
   * (Google etc.) which doesn't expose the backend UUID.
   */
  userDetail: 'userDetail',
  refreshToken: 'refreshToken',
  /** Ephemeral key used for cross-tab login/logout broadcasts. */
  sessionSync: 'auth_session_sync',
  /** Persists the user's selected UI theme ("light" | "dark"). */
  theme: 'theme',
  /**
   * Marks that `theme` above was set by an EXPLICIT in-app action (the
   * header `ThemeMode` toggle / `setTheme`), as opposed to merely being
   * derived once from `prefers-color-scheme`. Without this flag, a `theme`
   * entry is not trusted as a standing preference and the OS/browser
   * dark-mode setting is followed live instead — see `ThemeContext.tsx`.
   * React-only, no Vue parity required (Vue owns its own theme storage).
   */
  themeExplicit: 'rw.theme.explicit',
  /** Persists the user's selected i18n locale (e.g. "en-US"). */
  locale: 'locale',
  /**
   * Persists the AsideMenu panel open/collapsed state ("open" | "closed")
   * across page refreshes. React-only — no Vue parity required, the Vue
   * AsideMenu controls its own collapse state independently.
   */
  asideMenuPanelOpen: 'rw.asideMenu.panelOpen',
  /*
   * Emulation keys — these MUST stay byte-for-byte identical to the keys used
   * by RW-ERP-Service/src/types/constants/local-storage-constants.ts so that
   * starting an emulation in React is observed by the Vue ERP iframe and vice
   * versa. Source of truth for the strings: the Vue file referenced above.
   */
  isEmulate: 'isEmulate',
  emulatedUserId: 'emulatedUserId',
  emulatedUserName: 'emulatedUserName',
  emulatedUserDetail: 'emulatedUserDetail',
  emulatedUserPermission: 'emulatedPermission',
  /**
   * React-side localStorage mirror of the cached permission snapshot for the
   * REAL (non-emulated) user. Written alongside the IDB record so the menu
   * can paint synchronously on first frame.
   *
   * Consumed by `PermissionContext`, `loadMasterVersions`,
   * `loadPermissions`, `loadPermissionSlices`, and `devCacheDebug`.
   */
  permissionSnapshot: 'rw.permission.snapshot',
  /**
   * React-side localStorage mirror for the EMULATED user's permission
   * snapshot. Kept in a separate key so toggling emulation off doesn't
   * clobber the real user's mirror.
   */
  permissionSnapshotEmulated: 'rw.permission.snapshot.emulated',
  /**
   * React-ONLY marker (NO Vue parity — do not mirror to the Vue ERP). Records
   * the `emulatedUserId` whose emulation was just ended from React. Because
   * clearing Vue's emulation cross-origin is best-effort (iframe sync to
   * `end-emulation.html`) and may not be reflected by Vue's next probe
   * immediately, this marker tells `applyVueEmulationIfActive()` to suppress
   * re-mirroring that exact emulated user — preventing a just-ended emulation
   * from bouncing back on reload/focus. Self-clears once Vue stops emulating or
   * switches to a different user, and is cleared on a fresh React-initiated
   * emulation. See utils/emulation.ts.
   */
  emulationDismissedVueUserId: 'rw.emulation.dismissedVueUserId',
  /** 
   * Persists the SPI "PO Drafts" line items so they survive a hard refresh and
   * are shared across tabs on this origin (same-origin localStorage + the
   * `storage` event sync the draft list between open SPI tabs). React-only —
   * the SPI feature has no Vue counterpart.
   */
  spiPoDrafts: 'rw.spi.poDrafts',
  ceoPricingListingColumns: 'rw.ceoPricing.listingColumnWidths',
  /**
   * Persists the user's CEO Pricing listing column config — both the order
   * (from the show/hide popup drag-reorder) and which columns are hidden.
   * Shape: `{ order: string[]; hidden: string[] }`.
   */
  ceoPricingListingColumnConfig: 'rw.ceoPricing.listingColumnConfig',
  /**
   * What the server holds for this user's listing layout, cached so the page
   * does not ask on every load. Also records that we asked and found nothing,
   * so a user who has never customised does not re-ask on every load. Entries
   * carry a timestamp and are honoured for `CACHE_TTL_MS` only — a layout saved
   * from another browser or device is picked up on a later session.
   */
  ceoPricingColumnSettingsCache: 'rw.ceoPricing.columnSettingsCache',
  ceoPricingDetailColumnWidths: 'rw.ceoPricing.detailColumnWidths',
  /**
   * The detail grid's column config — display order and hidden columns, from
   * the same show/hide popup the listing uses. Separate from the widths key so
   * a user who only resizes never writes a config, and vice versa.
   * Shape: `{ order: string[]; hidden: string[] }`.
   */
  ceoPricingDetailColumnConfig: 'rw.ceoPricing.detailColumnConfig',
  /** Column widths for the slide-over family/compatibility product table. */
  ceoPricingSlidePanelColumns: 'rw.ceoPricing.slidePanelColumnWidths',
  /** Column widths for the SKU detail-panel table. */
  ceoPricingSkuDetailColumns: 'rw.ceoPricing.skuDetailColumnWidths',
  /** Column widths for the detail-panel cost-history table. */
  ceoPricingCostHistoryColumns: 'rw.ceoPricing.costHistoryColumnWidths',
  /** Column widths for the variation-group panel table. */
  ceoPricingVariationGroupColumns: 'rw.ceoPricing.variationGroupColumnWidths',
  /** Column widths for the variation drawer (Shopify/Amazon/Compatibility) table. */
  ceoPricingVariationDrawerColumns: 'rw.ceoPricing.variationDrawerColumnWidths',
  /** sessionStorage cache of CEO Pricing reference maps (UUID → display name). */
  ceoPricingReferenceMaps: 'rw.ceoPricing.referenceMaps.v6',
  /**
   * sessionStorage: the master versions (`vendor`, `category`, …) the CEO
   * reference maps above were built from. Compared against
   * `/v1/common/master/version` on every CEO page load; a mismatch drops the
   * maps and refetches master. Kept beside the maps, NOT in the shared
   * IndexedDB version baseline, because that baseline is advanced by other
   * consumers of the same entities and would mark this cache fresh without
   * ever refreshing it.
   */
  ceoPricingReferenceVersions: 'rw.ceoPricing.referenceVersions',
  /**
   * sessionStorage recovery key written by `public/auth-sync/handoff.html`
   * the moment it resolves a `?redirect=` target. If a later load of that
   * same static page arrives with the query string missing entirely (seen
   * in practice: the tab briefly shows the bare `/auth-sync/handoff` path,
   * no query, before landing on the wrong page), it recovers the intended
   * destination from here instead of silently defaulting to `/`. Also read
   * by `NotFoundRedirect` (`src/routes/AppRoutes.tsx`) for the same reason
   * when the SPA fallback (no `.html`) is what actually got hit.
   */
  authSyncRedirectRecovery: 'rw.authSyncRedirect',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

/**
 * String values used with the `STORAGE_KEYS.isEmulate` flag. The localStorage
 * surface is text-only, so the truthy/falsy state is encoded as these literal
 * strings — matching the Vue ERP exactly (`'true'` vs anything else).
 */
export const EMULATION_FLAG = {
  ON: 'true',
  OFF: 'false',
} as const;
